// Advertiser account and user types — schemas + inferred types.
// Used across backend, admin panel, and advertiser portal.
// The schemas are authoritative — the TS types are derived via z.infer<>.

import { z } from 'zod';

export const AdvertiserAccountStatusSchema = z.enum(['pending', 'active', 'suspended', 'rejected']);
export type AdvertiserAccountStatus = z.infer<typeof AdvertiserAccountStatusSchema>;

export const AdvertiserUserRoleSchema = z.enum(['owner', 'member']);
export type AdvertiserUserRole = z.infer<typeof AdvertiserUserRoleSchema>;

export const AdvertiserApplicationDataSchema = z.object({
  companyName: z.string(),
  contactName: z.string(),
  contactEmail: z.string(),
  website: z.string().optional(),
  industry: z.string().optional(),
  adBudgetRange: z.string().optional(),
  description: z.string(),
});
export type AdvertiserApplicationData = z.infer<typeof AdvertiserApplicationDataSchema>;

// There is deliberately NO shared `AdvertiserAccount` wire schema here.
//
// One existed and was removed: it had zero type consumers, and its single runtime use was a
// `.parse()` in the content seeder that validated an intermediate literal which was never the
// object written (the write added `balanceCents` and swapped the ISO strings for
// `serverTimestamp()` — so the persisted document would have FAILED that same schema).
//
// It also drifted from the one response it purported to describe: the admin endpoint ships
// `balanceCents`, which the schema omitted, and emits `createdAt`/`updatedAt` as possibly
// undefined where the schema required strings. Being wrong in a place people trust is worse than
// being absent, and it twice got flagged as a duplicate of the backend's persisted shape, costing
// a full investigation to refute.
//
// The contract for an advertiser account response is `toAdvertiserAccountResponse` in
// apps/backend/src/routes/admin.ts — an explicit allowlist projection, and currently the only
// surface that returns an account at all. If a second consumer ever appears (a typed admin client,
// or an advertiser-facing account screen), declare the shape then, against the real response.
//
// The status / application-data / apply-request / forgot-password schemas below all have real
// consumers and stay.

// API request types

// Ceilings for the free-text fields of the public application form. POST /advertiser/apply is
// unauthenticated, stores every one of these, and mails two of them (contactName, companyName) to
// an address the applicant chose — so each needs a bound that is not "whatever fits in the request
// body". Exported so the form's maxLength attributes cannot drift from what the server accepts.
export const ADVERTISER_APPLY_MAX_LENGTHS = {
  companyName: 150,
  contactName: 100,
  // RFC 5321's limit on an address; anything longer is not deliverable.
  contactEmail: 254,
  website: 2048,
  industry: 100,
  adBudgetRange: 100,
  description: 5000,
} as const;

// Exported for the same reason as the max lengths: the form's minLength used to restate it.
export const ADVERTISER_APPLY_MIN_PASSWORD_LENGTH = 12;

export const AdvertiserApplyRequestSchema = z.object({
  companyName: z.string().trim().min(1).max(ADVERTISER_APPLY_MAX_LENGTHS.companyName),
  contactName: z.string().trim().min(1).max(ADVERTISER_APPLY_MAX_LENGTHS.contactName),
  // Validated as an email because it is handed straight to firebaseAdmin.auth().createUser() in
  // routes/advertiser.ts — a bare z.string() meant a malformed address failed deep in the Admin
  // SDK instead of as a clean 400.
  contactEmail: z.string().email().max(ADVERTISER_APPLY_MAX_LENGTHS.contactEmail),
  // POST /advertiser/apply is the ONE signup path the backend actually owns: it Zod-validates and
  // then calls createUser() itself, rather than the client talking to Firebase Auth directly. So
  // unlike the consumer path, a server-side minimum is actually enforceable here — and it was a
  // bare z.string(), i.e. nothing above Firebase's own 6-character floor. 12 rather than the
  // consumer app's 8 because these accounts hold a Stripe balance and run campaigns that spend
  // real money. Applies to NEW applications only; existing passwords are never revalidated, so no
  // advertiser is locked out.
  password: z
    .string()
    .min(
      ADVERTISER_APPLY_MIN_PASSWORD_LENGTH,
      `Password must be at least ${ADVERTISER_APPLY_MIN_PASSWORD_LENGTH} characters`,
    ),
  // Deliberately NOT required to be an http(s) URL: the form field is free text, and "acme.com"
  // is what people type. It is only ever shown to an admin, as text.
  website: z.string().trim().max(ADVERTISER_APPLY_MAX_LENGTHS.website).optional(),
  industry: z.string().trim().max(ADVERTISER_APPLY_MAX_LENGTHS.industry).optional(),
  adBudgetRange: z.string().trim().max(ADVERTISER_APPLY_MAX_LENGTHS.adBudgetRange).optional(),
  description: z.string().trim().min(1).max(ADVERTISER_APPLY_MAX_LENGTHS.description),
});
export type AdvertiserApplyRequest = z.infer<typeof AdvertiserApplyRequestSchema>;

// POST /advertiser/forgot-password. The address becomes a rate-limit key and is handed to the
// Admin SDK, so it is validated and bounded first.
export const AdvertiserForgotPasswordRequestSchema = z.object({
  email: z.string().trim().email().max(ADVERTISER_APPLY_MAX_LENGTHS.contactEmail),
});
